<template>
    <transition name="settings-drawer">
        <div v-if="scenario" class="settings-drawer" key="settings-drawer">
            <div class="settings-drawer__backdrop" @click="$emit('close')"></div>
            <aside
                class="settings-drawer__panel drawer"
                :class="{ 'drawer--wide': scenario.meta.settingsType === 'deauth' }"
                role="dialog"
                aria-modal="true"
            >
                <div class="drawer-head">
                    <div>
                        <div class="drawer-title">{{ scenario.name }}</div>
                        <div class="drawer-sub">{{ scenario.meta.drawerDescription || scenario.meta.description }}</div>
                    </div>
                    <button class="drawer-x" aria-label="Закрыть" @click="$emit('close')">&times;</button>
                </div>
                <div class="drawer-body">
                    <StarterPackDrawerBattle
                        v-if="scenario.meta.settingsType === 'battle'"
                        :scenario="scenario"
                        :triggers-url="clientNotificationsTriggersUrl"
                    />

                    <div v-if="scenario.meta.settingsType === 'deauth'" class="drawer-deauth-block">
                        <StarterPackLoader
                            v-if="deauthLoading"
                            compact
                            label="Загружаем..."
                        />
                        <DeauthNotificationRecipientsBlock
                            v-else
                            :phone-validator="phoneValidator"
                            :deauth-contacts-value="deauthContactsValue"
                            :deauth-contacts="deauthContacts"
                            :deauth-all-groups="deauthAllGroups"
                            :access-error-message="deauthAccessErrorMessage"
                            :can-manage-deauth-recipients="canManageDeauthRecipients"
                            :is-super-user="isSuperUser"
                            :update-deauth-contacts-value="updateDeauthContactsValue"
                            :remove-deauth-contact="removeDeauthContact"
                            :verify-deauth-contact="verifyDeauthContact"
                            :deauth-verifying-indices="deauthVerifyingIndices"
                            :send-to-report-recipients="sendToReportRecipients"
                            :update-send-to-report-recipients="updateSendToReportRecipients"
                        />
                    </div>

                    <StarterPackDrawerCascade
                        v-if="scenario.meta.settingsType === 'cascade'"
                        ref="cascadeDrawer"
                        :project-id="projectId"
                        :fine-tune-url="redirectUrl"
                        @channels-change="handleCascadeReadinessChange"
                        @toast="$emit('toast', $event)"
                    />

                    <StarterPackDrawerComm
                        v-if="scenario.meta.settingsType === 'comm'"
                        :comm-channels="commChannels"
                        :selected-comm-channel-id="selectedCommChannelId"
                        :selected-comm-channel="selectedCommChannel"
                        :comm-help-url="commHelpUrl"
                        @select-channel="$emit('select-comm-channel', $event)"
                        @phones-updated="$emit('phones-updated', $event)"
                    />

                    <StarterPackDrawerMaster15
                        v-if="scenario.meta.settingsType === 'master15'"
                        :scenario="scenario"
                        :service-list="serviceList"
                        :services-loading="servicesLoading"
                    />

                    <StarterPackDrawerRedirect
                        v-if="scenario.meta.settingsType === 'redirect-info'"
                        :scenario="scenario"
                        :redirect-url="redirectUrl"
                    />
                </div>
                <div v-if="showActionFooter" class="drawer-foot">
                    <button class="btn-ghost" @click="$emit('close')">Отмена</button>
                    <v-tooltip bottom :disabled="!launchBlocked">
                        <template v-slot:activator="{ on, attrs }">
                            <span class="drawer-launch-wrap" v-bind="attrs" v-on="on">
                                <button class="btn-primary" :disabled="launchDisabled" @click="$emit('launch')">
                                    {{ launchButtonLabel }}
                                </button>
                            </span>
                        </template>
                        <span>{{ launchBlockedHint }}</span>
                    </v-tooltip>
                </div>
            </aside>
        </div>
    </transition>
</template>

<script>
import DeauthNotificationRecipientsBlock from '@/components/ClientNotifications/NotificationSettingsBlocks/DeauthNotificationRecipientsBlock.vue'
import StarterPackDrawerBattle from './drawer/StarterPackDrawerBattle.vue'
import StarterPackDrawerCascade from './drawer/StarterPackDrawerCascade.vue'
import StarterPackDrawerComm from './drawer/StarterPackDrawerComm.vue'
import StarterPackDrawerMaster15 from './drawer/StarterPackDrawerMaster15.vue'
import StarterPackDrawerRedirect from './drawer/StarterPackDrawerRedirect.vue'
import StarterPackLoader from './StarterPackLoader.vue'
import { getCascadeScenarioRefreshKey, hasCascadeReadinessChanged } from '../utils/cascadeDrawerSync'
import { isScenarioActive } from '../utils/scenarioUi'
import { STARTER_PACK_SAVE_ACCESS_DENIED_HINT } from '../constants'

export default {
    name: 'StarterPackSettingsDrawer',
    components: {
        DeauthNotificationRecipientsBlock,
        StarterPackDrawerBattle,
        StarterPackDrawerCascade,
        StarterPackDrawerComm,
        StarterPackDrawerMaster15,
        StarterPackDrawerRedirect,
        StarterPackLoader,
    },
    props: {
        scenario: {
            type: Object,
            default: null,
        },
        launching: {
            type: Boolean,
            default: false,
        },
        deauthLoading: {
            type: Boolean,
            default: false,
        },
        servicesLoading: {
            type: Boolean,
            default: false,
        },
        clientNotificationsTriggersUrl: {
            type: String,
            required: true,
        },
        redirectUrl: {
            type: String,
            default: '',
        },
        phoneValidator: {
            type: Function,
            required: true,
        },
        deauthContactsValue: {
            type: Array,
            default: () => [],
        },
        deauthContacts: {
            type: Array,
            default: () => [],
        },
        deauthAllGroups: {
            type: Array,
            default: () => [],
        },
        deauthAccessErrorMessage: {
            type: String,
            default: '',
        },
        canManageDeauthRecipients: {
            type: Boolean,
            default: false,
        },
        isSuperUser: {
            type: Boolean,
            default: false,
        },
        updateDeauthContactsValue: {
            type: Function,
            required: true,
        },
        removeDeauthContact: {
            type: Function,
            required: true,
        },
        verifyDeauthContact: {
            type: Function,
            required: true,
        },
        deauthVerifyingIndices: {
            type: Array,
            default: () => [],
        },
        sendToReportRecipients: {
            type: Boolean,
            default: false,
        },
        updateSendToReportRecipients: {
            type: Function,
            required: true,
        },
        commChannels: {
            type: Array,
            default: () => [],
        },
        selectedCommChannelId: {
            type: [String, null],
            default: null,
        },
        selectedCommChannel: {
            type: Object,
            default: null,
        },
        commHelpUrl: {
            type: String,
            required: true,
        },
        serviceList: {
            type: Array,
            default: () => [],
        },
        projectId: {
            type: String,
            default: '',
        },
        canSaveSettings: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            cascadeHasChannel: null,
        }
    },
    computed: {
        isCascadeDrawer() {
            return this.scenario?.meta?.settingsType === 'cascade'
        },
        cascadeScenarioRefreshKey() {
            return getCascadeScenarioRefreshKey(this.scenario)
        },
        isBattleDrawer() {
            return this.scenario?.meta?.settingsType === 'battle'
        },
        showActionFooter() {
            return this.scenario?.action !== 'redirect'
        },
        cascadeLaunchBlocked() {
            return this.isCascadeDrawer && !this.cascadeHasChannel
        },
        battleLaunchBlocked() {
            return this.isBattleDrawer && this.scenario?.settings?.mode !== 'battle'
        },
        launchBlocked() {
            return !this.canSaveSettings || this.cascadeLaunchBlocked || this.battleLaunchBlocked
        },
        launchBlockedHint() {
            if (!this.canSaveSettings) {
                return STARTER_PACK_SAVE_ACCESS_DENIED_HINT
            }
            if (this.cascadeLaunchBlocked) {
                return 'Подключите канал и задайте приоритет маркетинговых рассылок'
            }
            if (this.battleLaunchBlocked) {
                return 'Для запуска выберите «Боевой» режим'
            }
            return ''
        },
        drawerContentLoading() {
            return this.deauthLoading || this.servicesLoading
        },
        launchDisabled() {
            return this.launching || this.drawerContentLoading || this.launchBlocked
        },
        launchButtonLabel() {
            const isActive = isScenarioActive(this.scenario)
            if (this.launching) {
                return isActive ? 'Сохранение...' : 'Запуск...'
            }
            if (this.drawerContentLoading) {
                return 'Загрузка...'
            }
            return isActive ? 'Сохранить' : 'Запустить'
        },
    },
    watch: {
        cascadeScenarioRefreshKey(scenarioId) {
            if (!scenarioId) {
                this.cascadeHasChannel = null
                return
            }

            this.cascadeHasChannel = null
            this.$nextTick(() => {
                this.$refs.cascadeDrawer?.refreshOnOpen?.()
            })
        },
    },
    methods: {
        handleCascadeReadinessChange(isReady) {
            if (!hasCascadeReadinessChanged(this.cascadeHasChannel, isReady)) {
                return
            }

            this.cascadeHasChannel = isReady
            this.$emit('cascade-readiness-change', isReady)
        },
    },
}
</script>

<style lang="scss" scoped>
@import '@/vars.sass';

.settings-drawer {
    position: fixed;
    inset: 0;
    z-index: 1000;
}

.settings-drawer__backdrop {
    position: absolute;
    inset: 0;
    background: rgba(26, 26, 46, 0.44);
}

.settings-drawer__panel {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    overflow: hidden;
}

.settings-drawer-enter-active,
.settings-drawer-leave-active {
    .settings-drawer__backdrop {
        transition: opacity 0.25s ease;
    }

    .settings-drawer__panel {
        transition: transform 0.25s ease;
    }
}

.settings-drawer-enter .settings-drawer__backdrop,
.settings-drawer-leave-to .settings-drawer__backdrop {
    opacity: 0;
}

.settings-drawer-enter .settings-drawer__panel,
.settings-drawer-leave-to .settings-drawer__panel {
    transform: translateX(100%);
}

.drawer {
    display: flex;
    flex-direction: column;
    width: 440px;
    max-width: 92vw;
    background: #fff;
    box-shadow: -8px 0 40px rgba(26, 26, 46, 0.18);
}

.drawer--wide {
    width: min(720px, 96vw);
}

.drawer-deauth-block {
    min-width: 0;
}

.drawer-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 22px 24px 18px;
    border-bottom: 1px solid $border-new;
}

.drawer-title {
    color: $dark-new;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 18px;
    font-weight: 900;
    line-height: 1.15;
    letter-spacing: -0.02em;
}

.drawer-sub {
    margin-top: 6px;
    color: $muted-new;
    font-size: 12.5px;
    line-height: 1.5;
}

.drawer-x {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    color: $muted-new;
    background: #f4f6fa;
    border: 1px solid $border-new;
    border-radius: 9px;
    cursor: pointer;
    font-size: 19px;
}

.drawer-body {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 22px;
    min-width: 0;
    padding: 22px 24px;
    overflow-x: hidden;
    overflow-y: auto;
}

.drawer-foot {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 16px 24px;
    border-top: 1px solid $border-new;
}

.btn-ghost,
.btn-primary {
    padding: 11px 20px;
    border-radius: 10px;
    cursor: pointer;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 13px;
    font-weight: 700;
}

.btn-ghost {
    color: $text-color-new;
    background: #fff;
    border: 1.4px solid $border-new;
}

.btn-primary {
    color: #fff;
    background: $main-new;
    border: 0;

    &:disabled {
        opacity: 0.55;
        cursor: default;
    }
}

.drawer-launch-wrap {
    display: inline-flex;
}
</style>
